import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, Plus, Trash2, MapPin, Calendar, Star, LogOut, Pencil, MessageSquare, ChevronDown, ChevronUp, User } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", 
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", 
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", 
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", 
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", 
  "New Hampshire", "New Jersey", "New Mexico", "New York", 
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", 
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", 
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", 
  "West Virginia", "Wisconsin", "Wyoming"
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingJudge, setEditingJudge] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [expandedJudge, setExpandedJudge] = useState(null);
  const [judgeReviews, setJudgeReviews] = useState({});
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [deleteReviewConfirm, setDeleteReviewConfirm] = useState(null);
  const [deleteReviewLoading, setDeleteReviewLoading] = useState(false);

  // New judge form
  const [newJudge, setNewJudge] = useState({
    name: "",
    location: "",
    huntTest: false,
    fieldTrial: false,
  });
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setIsAuthenticated(true);
      fetchJudges();
    } catch (error) {
      localStorage.removeItem("adminToken");
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { token } = response.data;
      localStorage.setItem("adminToken", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setIsAuthenticated(true);
      toast.success("Admin login successful");
      fetchJudges();
    } catch (error) {
      toast.error("Invalid admin password");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    delete axios.defaults.headers.common["Authorization"];
    setIsAuthenticated(false);
    setJudges([]);
    toast.success("Logged out");
  };

  const fetchJudges = async () => {
    try {
      const response = await axios.get(`${API}/judges?limit=2000&sort_by_lastname=true`);
      setJudges(response.data);
    } catch (error) {
      console.error("Failed to fetch judges:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJudgeReviews = async (judgeId) => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/judges/${judgeId}/reviews`);
      setJudgeReviews(prev => ({ ...prev, [judgeId]: response.data }));
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      toast.error("Failed to load reviews");
    } finally {
      setReviewsLoading(false);
    }
  };

  const toggleJudgeReviews = (judgeId) => {
    if (expandedJudge === judgeId) {
      setExpandedJudge(null);
    } else {
      setExpandedJudge(judgeId);
      if (!judgeReviews[judgeId]) {
        fetchJudgeReviews(judgeId);
      }
    }
  };

  const handleAddJudge = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      const eventsArray = [];
      if (newJudge.huntTest) eventsArray.push("Hunt Test");
      if (newJudge.fieldTrial) eventsArray.push("Field Trial");

      if (eventsArray.length === 0) {
        toast.error("Please select at least one event type");
        setAddLoading(false);
        return;
      }

      await axios.post(`${API}/admin/judges`, {
        name: newJudge.name,
        location: newJudge.location,
        events_judged: eventsArray,
      });

      toast.success("Judge added successfully");
      setNewJudge({ name: "", location: "", huntTest: false, fieldTrial: false });
      fetchJudges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add judge");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteJudge = async (judgeId) => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/judges/${judgeId}`);
      toast.success("Judge deleted successfully");
      setShowDeleteConfirm(null);
      fetchJudges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete judge");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditJudge = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const eventsArray = [];
      if (editingJudge.huntTest) eventsArray.push("Hunt Test");
      if (editingJudge.fieldTrial) eventsArray.push("Field Trial");

      if (eventsArray.length === 0) {
        toast.error("Please select at least one event type");
        setEditLoading(false);
        return;
      }

      await axios.put(`${API}/admin/judges/${editingJudge.id}`, {
        name: editingJudge.name,
        location: editingJudge.location,
        events_judged: eventsArray,
      });

      toast.success("Judge updated successfully");
      setEditingJudge(null);
      fetchJudges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update judge");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId, judgeId) => {
    setDeleteReviewLoading(true);
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      toast.success("Review deleted successfully");
      setDeleteReviewConfirm(null);
      // Refresh reviews for this judge
      fetchJudgeReviews(judgeId);
      // Refresh judges list to update review counts
      fetchJudges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete review");
    } finally {
      setDeleteReviewLoading(false);
    }
  };

  const openEditDialog = (judge) => {
    const events = judge.events_judged || [];
    setEditingJudge({
      ...judge,
      huntTest: events.includes("Hunt Test"),
      fieldTrial: events.includes("Field Trial")
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white gold-accent-top shadow-lg rounded-sm p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-gold" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy">
                Admin Login
              </h1>
              <p className="text-slate-600 mt-2">
                Enter the admin password to manage judges
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Admin Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-navy hover:bg-navy/90"
                disabled={authLoading}
                data-testid="admin-login-btn"
              >
                {authLoading ? "Logging in..." : "Login"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate("/")}
                className="text-sm text-slate-500 hover:text-navy"
              >
                Back to home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-off-white py-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy">
              Admin Dashboard
            </h1>
            <p className="text-slate-600">Manage judges for RateMyJudges</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-navy text-navy hover:bg-navy hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Add Judge Form */}
        <div className="bg-white gold-accent-top shadow-lg rounded-sm p-6 mb-8">
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">
            <Plus className="w-5 h-5 inline mr-2" />
            Add New Judge
          </h2>
          <form onSubmit={handleAddJudge} className="grid md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="name">Judge Name *</Label>
              <Input
                id="name"
                value={newJudge.name}
                onChange={(e) =>
                  setNewJudge({ ...newJudge, name: e.target.value })
                }
                placeholder="Full name"
                required
                data-testid="admin-judge-name-input"
              />
            </div>
            <div>
              <Label htmlFor="location">State *</Label>
              <Select
                value={newJudge.location}
                onValueChange={(value) =>
                  setNewJudge({ ...newJudge, location: value })
                }
              >
                <SelectTrigger className="w-full" data-testid="admin-judge-location-select">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="events">Event Type *</Label>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="huntTest"
                    checked={newJudge.huntTest}
                    onCheckedChange={(checked) =>
                      setNewJudge({ ...newJudge, huntTest: checked })
                    }
                    data-testid="admin-hunt-test-checkbox"
                  />
                  <label htmlFor="huntTest" className="text-sm font-medium cursor-pointer">
                    Hunt Test
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="fieldTrial"
                    checked={newJudge.fieldTrial}
                    onCheckedChange={(checked) =>
                      setNewJudge({ ...newJudge, fieldTrial: checked })
                    }
                    data-testid="admin-field-trial-checkbox"
                  />
                  <label htmlFor="fieldTrial" className="text-sm font-medium cursor-pointer">
                    Field Trial
                  </label>
                </div>
              </div>
            </div>
            <div className="flex items-end">
              <Button
                type="submit"
                className="w-full bg-gold text-navy hover:bg-gold/90 font-semibold"
                disabled={addLoading}
                data-testid="admin-add-judge-btn"
              >
                {addLoading ? "Adding..." : "Add Judge"}
              </Button>
            </div>
          </form>
        </div>

        {/* Judges List */}
        <div className="bg-white shadow rounded-sm p-6">
          <h2 className="font-heading text-xl font-semibold text-navy mb-4">
            All Judges ({judges.length})
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-slate-100 animate-pulse rounded" />
              ))}
            </div>
          ) : judges.length > 0 ? (
            <div className="space-y-3">
              {judges.map((judge) => (
                <div key={judge.id}>
                  <div
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-200 hover:border-gold transition-colors"
                    data-testid={`admin-judge-${judge.id}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-navy">{judge.name}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600 mt-1">
                        {judge.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {judge.location}
                          </span>
                        )}
                        {judge.events_judged?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {judge.events_judged.join(", ")}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {judge.avg_overall?.toFixed(1) || "0.0"} ({judge.review_count || 0} reviews)
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {judge.review_count > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-300 text-slate-600 hover:bg-slate-100"
                          onClick={() => toggleJudgeReviews(judge.id)}
                          data-testid={`toggle-reviews-${judge.id}`}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          {judge.review_count}
                          {expandedJudge === judge.id ? (
                            <ChevronUp className="w-4 h-4 ml-1" />
                          ) : (
                            <ChevronDown className="w-4 h-4 ml-1" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gold text-gold hover:bg-gold hover:text-navy"
                        onClick={() => openEditDialog(judge)}
                        data-testid={`edit-judge-${judge.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                        onClick={() => setShowDeleteConfirm(judge)}
                        data-testid={`delete-judge-${judge.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Reviews Section */}
                  {expandedJudge === judge.id && (
                    <div className="ml-4 mt-2 mb-4 p-4 bg-white border border-slate-200 rounded-sm">
                      <h4 className="font-semibold text-navy mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Reviews for {judge.name}
                      </h4>
                      {reviewsLoading ? (
                        <div className="animate-pulse space-y-2">
                          <div className="h-16 bg-slate-100 rounded" />
                          <div className="h-16 bg-slate-100 rounded" />
                        </div>
                      ) : judgeReviews[judge.id]?.length > 0 ? (
                        <div className="space-y-3">
                          {judgeReviews[judge.id].map((review) => (
                            <div
                              key={review.id}
                              className="p-3 bg-slate-50 rounded border-l-4 border-gold"
                              data-testid={`review-item-${review.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <User className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium text-navy">{review.display_name}</span>
                                    <span className="text-xs text-slate-500">
                                      {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 mb-2">
                                    <Star className="w-4 h-4 fill-gold-bright text-gold-bright" />
                                    <span className="font-semibold">{review.overall_rating}</span>
                                    <span className="text-xs text-slate-500 ml-2">
                                      F:{review.fairness} K:{review.knowledge} C:{review.communication} P:{review.professionalism}
                                    </span>
                                  </div>
                                  {review.comment && (
                                    <p className="text-sm text-slate-600">{review.comment}</p>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setDeleteReviewConfirm({ review, judgeId: judge.id })}
                                  data-testid={`delete-review-${review.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">No reviews found</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No judges yet. Add your first judge above!
            </div>
          )}
        </div>
      </div>

      {/* Delete Judge Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-navy">Delete Judge</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{showDeleteConfirm?.name}</strong>? 
              This will also delete all reviews for this judge. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleDeleteJudge(showDeleteConfirm?.id)}
              disabled={deleteLoading}
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? "Deleting..." : "Delete Judge"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Judge Dialog */}
      <Dialog open={!!editingJudge} onOpenChange={() => setEditingJudge(null)}>
        <DialogContent data-testid="edit-judge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">Edit Judge</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditJudge} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Judge Name *</Label>
              <Input
                id="edit-name"
                value={editingJudge?.name || ""}
                onChange={(e) =>
                  setEditingJudge({ ...editingJudge, name: e.target.value })
                }
                placeholder="Full name"
                required
                data-testid="edit-judge-name-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-location">State *</Label>
              <Select
                value={editingJudge?.location || ""}
                onValueChange={(value) =>
                  setEditingJudge({ ...editingJudge, location: value })
                }
              >
                <SelectTrigger className="w-full" data-testid="edit-judge-location-select">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-events">Event Type *</Label>
              <div className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-huntTest"
                    checked={editingJudge?.huntTest || false}
                    onCheckedChange={(checked) =>
                      setEditingJudge({ ...editingJudge, huntTest: checked })
                    }
                    data-testid="edit-hunt-test-checkbox"
                  />
                  <label htmlFor="edit-huntTest" className="text-sm font-medium cursor-pointer">
                    Hunt Test
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-fieldTrial"
                    checked={editingJudge?.fieldTrial || false}
                    onCheckedChange={(checked) =>
                      setEditingJudge({ ...editingJudge, fieldTrial: checked })
                    }
                    data-testid="edit-field-trial-checkbox"
                  />
                  <label htmlFor="edit-fieldTrial" className="text-sm font-medium cursor-pointer">
                    Field Trial
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setEditingJudge(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gold text-navy hover:bg-gold/90 font-semibold"
                disabled={editLoading}
                data-testid="save-edit-judge-btn"
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Review Confirmation Dialog */}
      <Dialog open={!!deleteReviewConfirm} onOpenChange={() => setDeleteReviewConfirm(null)}>
        <DialogContent data-testid="delete-review-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-navy">Delete Review</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this review by <strong>{deleteReviewConfirm?.review?.display_name}</strong>? 
              This will update the judge's ratings. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setDeleteReviewConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleDeleteReview(deleteReviewConfirm?.review?.id, deleteReviewConfirm?.judgeId)}
              disabled={deleteReviewLoading}
              data-testid="confirm-delete-review-btn"
            >
              {deleteReviewLoading ? "Deleting..." : "Delete Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
